import { baseConfig, DataLayerSettings } from './dataLayer';
import type { TrackingProps } from './types';

const RegexType = /$/;
const PLACEHOLDER_VAR = '%s';

type RouteProps = {
  path: string | typeof RegexType;
  /** ISO 639-1 language code */
  lang: 'de' | 'fr' | 'es' | 'it' | 'nl';
  tracking: TrackingProps;
};

const getFallbackRoute = (settings: DataLayerSettings, pathname: string) => {
  const config = {
    ...baseConfig,
    ...settings,
  };

  const fallback: RouteProps = {
    path: pathname,
    lang: 'de',
    tracking: {
      ...config,
      virtPath: '/cars/bm/Unknown',
      landingpageKategorie: '',
      websitebereich: 'Startseite, Mietwagen-Suche',
      englishPageName: 'd/funnel/unknown',
      landingPageType: 'index',
    },
  };

  return fallback;
};

const getRoutes = (settings: DataLayerSettings) => {
  const config = {
    ...baseConfig,
    ...settings,
  };

  const routes: RouteProps[] = [
    {
      path: '/',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/cars/bm/Index',
        landingpageKategorie: '',
        websitebereich: 'Startseite, Mietwagen-Suche',
        englishPageName: 'd/funnel/index',
        landingPageType: 'index',
      },
    },
    {
      path: '/404',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/errors/page-not-found?ec=errors',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/errors/page-not-found',
        landingPageType: 'error',
      },
    },
    {
      path: /\/(cars\/(.*))/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/cars/affiliate/Index',
        landingpageKategorie: '',
        websitebereich: 'Startseite, Mietwagen-Suche',
        englishPageName: 'd/funnel/index',
        landingPageType: 'index',
      },
    },
    {
      path: '/app.html',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/app.html',
        landingpageKategorie: '',
        websitebereich: 'Landingpage, App',
        englishPageName: 'd/seo/app',
        landingPageType: 'seo',
      },
    },
    {
      path: '/presse.html',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/presse.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/presse',
        landingPageType: 'seo',
      },
    },
    {
      path: '/floyt-plus.html',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/floyt-plus.html',
        landingpageKategorie: '',
        websitebereich: 'Landingpage, Floyt-plus',
        englishPageName: 'd/seo/app',
        landingPageType: 'seo',
      },
    },
    {
      path: '/mietwagen-anbieter.html',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/mietwagen-anbieter.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/partnerlist',
        landingPageType: 'index',
      },
    },
    {
      path: '/mietwagen-flughafen.html',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/mietwagen-flughafen.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/mietwagen/flughafen',
        landingPageType: 'index',
      },
    },
    {
      path: '/mietwagen-bahnhof.html',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/mietwagen-bahnhof.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/mietwagen/bahnhof',
        landingPageType: 'index',
      },
    },
    {
      path: '/mietwagen.html',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/mietwagen.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/weltweit',
        landingPageType: 'index',
      },
    },
    {
      path: '/transporter-mieten.html',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/transporter-mieten.html',
        landingpageKategorie: '',
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: 'd/seo/transporter-mieten',
        landingPageType: 'index',
      },
    },
    {
      path: '/newsletter',
      lang: 'de',
      tracking: {
        ...config,
        virtPath: '/newsletter',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/newsletter',
        landingPageType: 'index',
      },
    },
    {
      path: /\/(promo\/(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/promo/${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/promo/${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. transporter-mieten-berlin.html
      path: /\/(transporter-mieten-(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/transporter-mieten-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/transporter-mieten-${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. autovermieter-avis.html
      path: /\/(autovermieter-(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/partner-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/partner-${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. mietwagen-muenchen.html
      path: /\/(mietwagen-(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/mietwagen-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/mietwagen/${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. kleinbus-mieten.html
      path: /\/((.*)-mieten)\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/${PLACEHOLDER_VAR}-mieten.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. kleinbus-mieten-berlin.html
      path: /\/(kleinbus-mieten-(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/kleinbus-mieten-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/kleinbus-mieten-${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. kleinbus-mieten-berlin.html
      path: /\/(gelaendewagen-mieten-(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/gelaendewagen-mieten-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/gelaendewagen-mieten-${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. autovermietung-berlin.html
      path: /\/(autovermietung-(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/autovermietung-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/mietwagen/${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. rentacar-lax.html
      path: /\/(rentacar-(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/rentacar-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/rentacar/${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      path: /\/(rechtliches\/(.*))\.html/,
      lang: 'de',
      tracking: {
        ...config,
        virtPath: `/rechtliches/${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: `d/funnel/${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      path: /\/(infos-legales\/(.*))\.html/,
      lang: 'fr',
      tracking: {
        ...config,
        virtPath: `/infos-legales/${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: `d/funnel/${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      path: '/monde.html',
      lang: 'fr',
      tracking: {
        ...config,
        virtPath: '/monde.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/weltweit',
        landingPageType: 'index',
      },
    },
    {
      path: '/loueur-voiture.html',
      lang: 'fr',
      tracking: {
        ...config,
        virtPath: '/loueur-voiture.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/partnerlist',
        landingPageType: 'index',
      },
    },
    {
      path: '/location-voiture-aeroport.html',
      lang: 'fr',
      tracking: {
        ...config,
        virtPath: '/location-voiture-aeroport.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/mietwagen/flughafen',
        landingPageType: 'index',
      },
    },
    {
      path: '/location-voiture-gare.html',
      lang: 'fr',
      tracking: {
        ...config,
        virtPath: '/location-voiture-gare.html',
        landingpageKategorie: '',
        websitebereich: 'Content-Seite',
        englishPageName: 'd/seo/mietwagen/bahnhof',
        landingPageType: 'index',
      },
    },
    {
      // regex, e.g. loueur-jucy-rentals.html
      path: /\/(loueur-(.*))\.html/,
      lang: 'fr',
      tracking: {
        ...config,
        virtPath: `/partenaire-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/partenaire-${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
    {
      // regex, e.g. louer-gare-lille.html
      path: /\/(louer-(.*))\.html/,
      lang: 'fr',
      tracking: {
        ...config,
        virtPath: `/location-voiture-${PLACEHOLDER_VAR}.html`,
        landingpageKategorie: PLACEHOLDER_VAR,
        websitebereich: 'Landingpage, Mietwagen-Suche',
        englishPageName: `d/seo/location-voiture-${PLACEHOLDER_VAR}`,
        landingPageType: 'seo',
      },
    },
  ];

  return routes;
};

export { getRoutes, getFallbackRoute, RegexType, PLACEHOLDER_VAR };
